import { z } from 'zod';

export const EVENT_HASHTAG_MAX_COUNT = 3;
export const EVENT_HASHTAG_MAX_LEN = 40;

/** Разделитель сегментов: пробел (основной UX) и запятая (совместимость со старым вводом). */
const HASHTAG_SEGMENT_SEP = /[\s,]+/;

/** Один хэштег: без #, lowercase, буквы/цифры/_. */
export const EventHashtagSchema = z
  .string()
  .trim()
  .min(1)
  .max(EVENT_HASHTAG_MAX_LEN)
  .transform((s) => {
    const raw = s.startsWith('#') ? s.slice(1) : s;
    return raw.trim().toLowerCase();
  })
  .refine((s) => s.length > 0 && /^[\p{L}\p{N}_]+$/u.test(s), {
    message: 'invalid hashtag',
  });

export const EventHashtagsSchema = z.array(EventHashtagSchema).max(EVENT_HASHTAG_MAX_COUNT);

export const EventHashtagsRequiredSchema = z.array(EventHashtagSchema).length(EVENT_HASHTAG_MAX_COUNT);

export type ParseHashtagsErrorCode = 'empty' | 'too_few' | 'too_many' | 'invalid' | 'duplicate';

export type ParseHashtagsResult =
  | { ok: true; hashtags: [string, string, string] }
  | { ok: false; code: ParseHashtagsErrorCode };

export function hashtagParseErrorMessage(code: ParseHashtagsErrorCode): string {
  switch (code) {
    case 'empty':
    case 'too_few':
      return 'Нужно ровно 3 хэштега через пробел';
    case 'too_many':
      return 'Не больше 3 хэштегов';
    case 'duplicate':
      return 'Хэштеги не должны повторяться';
    case 'invalid':
      return 'Хэштег: только буквы, цифры и _ (без пробелов)';
  }
}

function tryParseHashtag(segment: string): string | null {
  const parsed = EventHashtagSchema.safeParse(segment);
  return parsed.success ? parsed.data : null;
}

export interface HashtagInputPreview {
  committed: string[];
  pending: string;
}

/**
 * Живой разбор ввода: готовые теги + незавершённый фрагмент.
 * Последний сегмент без завершающего разделителя засчитывается как тег, только если он
 * завершает набор из EVENT_HASHTAG_MAX_COUNT; иначе остаётся pending (ещё печатается).
 */
export function collectHashtagSegments(raw: string): HashtagInputPreview {
  const trimmed = raw.trim();
  if (!trimmed) return { committed: [], pending: '' };

  const parts = raw.split(HASHTAG_SEGMENT_SEP);
  const endsWithSep = /[\s,]$/.test(raw);
  const committed: string[] = [];
  let pending = '';

  for (let i = 0; i < parts.length; i++) {
    const segment = (parts[i] ?? '').trim();
    if (!segment) continue;

    const isLast = i === parts.length - 1;
    const tag = tryParseHashtag(segment);

    if (isLast && !endsWithSep) {
      // Хвостовой сегмент без разделителя коммитим, только если он завершает набор
      // из EVENT_HASHTAG_MAX_COUNT; иначе это ещё недопечатанный тег → pending.
      if (tag != null && !committed.includes(tag) && committed.length + 1 === EVENT_HASHTAG_MAX_COUNT) {
        committed.push(tag);
      } else {
        pending = segment;
      }
      continue;
    }

    if (tag && !committed.includes(tag)) {
      committed.push(tag);
    }
  }

  return {
    committed: committed.slice(0, EVENT_HASHTAG_MAX_COUNT),
    pending,
  };
}

/**
 * Разбирает строку «a b c» (или «a, b, c») в ровно 3 нормализованных хэштега.
 * Валидация не зависит от эвристики pending у превью: чистый split по пробелам/запятым,
 * ровно EVENT_HASHTAG_MAX_COUNT непустых частей, каждая валидна и без дублей.
 * Трейлинговый разделитель не обязателен — `a b c` считается полным набором.
 */
export function parseHashtagsInput(raw: string): ParseHashtagsResult {
  const trimmed = raw.trim();
  if (!trimmed) return { ok: false, code: 'empty' };

  const parts = trimmed
    .split(HASHTAG_SEGMENT_SEP)
    .map((s) => s.trim())
    .filter((s) => s.length > 0);

  if (parts.length < EVENT_HASHTAG_MAX_COUNT) return { ok: false, code: 'too_few' };
  if (parts.length > EVENT_HASHTAG_MAX_COUNT) return { ok: false, code: 'too_many' };

  const out: string[] = [];
  for (const part of parts) {
    const tag = tryParseHashtag(part);
    if (!tag) return { ok: false, code: 'invalid' };
    if (out.includes(tag)) return { ok: false, code: 'duplicate' };
    out.push(tag);
  }

  return { ok: true, hashtags: [out[0]!, out[1]!, out[2]!] };
}

/** @deprecated Используй collectHashtagSegments — алиас для обратной совместимости в UI. */
export function previewHashtagsFromInput(raw: string): HashtagInputPreview {
  return collectHashtagSegments(raw);
}

export function formatHashtagsForInput(hashtags: readonly string[]): string {
  return hashtags.slice(0, EVENT_HASHTAG_MAX_COUNT).join(' ');
}
